// LiveSession — WebSocket lifecycle, media plumbing, and workflow orchestration.
//
// State machine (`session.phase`) mirrors the design:
//   idle → media-ready → connecting → connected → speaking → finishing → finished → error
//
// We keep `phase` as the single source of truth and derive UI state from it,
// avoiding the boolean soup that the original implementation had to carry.
//
// Scan-isolation mode: when the server reports a workflow_state whose active
// step has `kind === "document_scan"`, this module suspends the normal
// observer media plumbing for the duration of the scan step:
//   - main-source video frames are NOT forwarded to the observer session
//   - mic chunks are NOT forwarded to the observer session
//   - auto-checks pause
// A separate scan loop captures a high-quality still from the configured
// Source 2 and sends it as a `document_scan_image` message. The server runs
// that image through a *separate* clean Live session and either materializes
// new workflow steps or reports unreadable. Once the workflow_state leaves
// the scan step, observer media plumbing resumes automatically.

import {
  AUTO_CHECK_INTERVAL_MS,
  AUTO_START_ACTION,
  DOCUMENT_SCAN_JPEG_QUALITY,
  DOCUMENT_SCAN_MAX_DIM,
  DOCUMENT_SCAN_RETRY_MS,
  DOCUMENT_SCAN_SOURCE_INDEX,
  RECV_RATE,
  SEND_CONTINUOUS_VIDEO_FRAMES,
  SEND_RATE,
  WS_DROP_AUDIO_BUFFER_BYTES,
  WS_DROP_IMAGE_BUFFER_BYTES,
} from "./constants.js";
import {
  handleServerDebug,
  logDebug,
  noteAudioChunkDropped,
  noteBrowserAudioChunk,
  noteBrowserImageFrame,
  noteImageFrameDropped,
  noteModelAudioChunk,
  setDebugCurrentView,
  setDebugWsState,
} from "./debug.js";
import { els } from "./dom.js";
import { getLanguage, t } from "./i18n.js";
import { AudioPlayer } from "./audio-player.js";
import { MicCapture } from "./mic-capture.js";
import { VideoSourceManager } from "./video-source-manager.js";
import {
  contextMessage,
  documentScanImageMessage,
  imageMessage,
  proofImageMessage,
  startMessage,
  stopMessage,
  textMessage,
} from "./protocol.js";
import { PackingPanel } from "./packing-panel.js";
import { TrainerBridge } from "./trainer-bridge.js";
import { ensureRuntimeConfig } from "./runtime-config.js";
import { resolveWsUrl } from "./ws-url.js";
import {
  appendMessage,
  renderStartStopButton,
  setState,
  setWorkflowControlsEnabled,
} from "./state.js";
import {
  buildModelStepInstruction,
  clearStepCardMotion,
  flashStepCardMotion,
  renderWorkflowState,
  selectedWorkflowId,
} from "./workflow-ui.js";

export class LiveSession {
  constructor({ runtimeConfig = null, recorder = null } = {}) {
    this.runtimeConfig = runtimeConfig;
    // Optional local-only recorder. LiveSession only calls a handful of
    // methods on it (isWorkflowArmed / startWorkflowCapture /
    // stopWorkflowCapture / syncSources) and is otherwise unaware of it.
    // The recorder never sees `this.ws`, never sends WebSocket messages,
    // and never makes network calls of its own.
    this.recorder = recorder;
    this.ws = null;
    this.player = new AudioPlayer(RECV_RATE);
    this.mic = new MicCapture(SEND_RATE);
    this.videoSources = new VideoSourceManager({
      container: els.videoSources,
      canvasEl: els.captureCanvas,
      addButton: els.addVideoSourceBtn,
      onChange: () => {
        this._syncMediaState();
        this._renderMediaButtons();
        // Recorder watches source changes so it can attach a recorder for
        // a slot that becomes active mid-recording. No-op when not
        // recording. Sync errors are logged and swallowed so they cannot
        // disrupt the existing media-state plumbing.
        if (this.recorder && typeof this.recorder.syncSources === "function") {
          try {
            this.recorder.syncSources(this.videoSources);
          } catch (e) {
            logDebug(
              "browser",
              "recorder_sync_failed",
              { message: e?.message || String(e) },
              "warn",
            );
          }
        }
      },
    });

    this.stepSignalTimers = { next: null, prev: null };
    this.interruptNoticeTimer = null;
    this.autoCheckTimer = null;

    // Phase machine + flags only used inside this module.
    this.phase = "idle";
    this.turnInProgress = false;
    this.workflowPromptShown = false;
    this.workflowStarted = false;
    this.workflowState = null;
    this.workflowFinishPending = false;
    this.workflowFinishedUi = false;
    this.workflowConfig = null;
    // Silent mode is frontend-only: when true the browser does not play the
    // model's audio. The model still streams audio and the backend is
    // unaffected. Defaults to off (audio plays); a workflow may override the
    // default and the user can toggle it at runtime.
    this.silentMode = false;
    this.closeReason = t("status.disconnected");
    this.mediaPrepared = false;
    this.preparedMode = "none";
    this.modelConnected = false;

    // Document-scan state. ``observerMediaPausedForScan`` is the only flag
    // the media-sender callbacks need to consult to know they should skip
    // forwarding to the observer session — keeping that one boolean check
    // out-of-loop avoids per-frame overhead.
    this.documentScanActive = false;
    this.documentScanTimer = null;
    this.documentScanAttempt = 0;
    this.documentScanWaiting = false;
    this.documentScanStepId = null;
    this.documentScanConfig = null;
    this.observerMediaPausedForScan = false;

    // Packing proof (workflows with `packing_proof`). The Trainer bridge only
    // exists when this page runs as a tab inside the how.fm Trainer; the panel
    // renders nothing for other workflows.
    this.trainer = new TrainerBridge();
    this.packingEnabled = false;
    this.proofImages = new Map(); // request_id -> { mime_type, data, at }
    this.proofClientSeq = 0;
    this.lastContextSignature = null;
    this.proofSourceWarnedAt = 0;
    this.packingPanel = new PackingPanel({
      container: els.packingPanel,
      onCapture: () => void this.captureWorkerProof("manual"),
    });
    this.packingPanel.setTrainerAvailable(this.trainer.available);
    this.trainer.onContext((payload) => this._onTrainerContext(payload));
    this.trainer.onTrigger((trigger) => this._onTrainerTrigger(trigger));

    this._renderMediaButtons();
    this._renderMicBtn();
    this._renderSilentBtn();
  }

  setWorkflowConfig(workflow) {
    const prevId = this.workflowConfig?.id;
    this.workflowConfig = workflow || null;
    // Apply the workflow's silent-mode default only when the workflow actually
    // changes, so a manual toggle survives unrelated re-fetches (e.g. a
    // language switch re-fetches the same workflow).
    const nextId = this.workflowConfig?.id;
    if (workflow && nextId !== prevId) {
      const def = workflow.silent_mode_default;
      this.setSilentMode(def === undefined ? false : !!def, "workflow_default");
    }
    if (this.videoSources && typeof this.videoSources.setObserverVideoConfig === "function") {
      this.videoSources.setObserverVideoConfig(this.workflowConfig?.observer_video || null);
    }
    this.packingEnabled = !!this.workflowConfig?.packing_proof;
    this.lastContextSignature = null;
    if (this.packingPanel) {
      this.packingPanel.setWorkflow(this.workflowConfig);
      if (this.trainer?.latest) this.packingPanel.setContext(this.trainer.latest.context);
    }
  }

  setSilentMode(on, reason = "user") {
    this.silentMode = !!on;
    // Turning silent mode on should also silence whatever is currently
    // playing, not just future chunks.
    if (this.silentMode) this.player.stopAll();
    this._renderSilentBtn();
    logDebug("browser", "silent_mode_changed", { silent: this.silentMode, reason });
  }

  toggleSilentMode() {
    this.setSilentMode(!this.silentMode, "user_toggle");
  }

  _renderSilentBtn() {
    if (!els.silentModeBtn) return;
    els.silentModeBtn.dataset.active = String(this.silentMode);
    els.silentModeBtn.textContent = this.silentMode
      ? t("controls.silent_mode_on")
      : t("controls.silent_mode_off");
  }

  // ---- media plumbing -------------------------------------------------------

  _currentMode() {
    return this.videoSources ? this.videoSources.currentMode() : "none";
  }
  _hasAudio() {
    return !!this.mic.stream;
  }
  _hasVideo() {
    return !!this.videoSources && this.videoSources.hasActiveSources();
  }
  _hasAnyMedia() {
    return this._hasAudio() || this._hasVideo();
  }
  _syncMediaState() {
    this.mediaPrepared = this._hasAnyMedia();
    this.preparedMode = this._currentMode();
  }
  _renderMediaButtons() {
    this._syncMediaState();
    const audioActive = this._hasAudio();
    if (els.captureAudioBtn) {
      els.captureAudioBtn.dataset.active = String(audioActive);
      els.captureAudioBtn.textContent = audioActive
        ? t("controls.stop_audio")
        : t("controls.capture_audio");
    }
    if (this.videoSources) this.videoSources.updateToolbar();
  }

  async toggleAudio() {
    if (this._hasAudio()) {
      await this.stopAudio();
      return;
    }
    await this.captureAudio();
  }

  async captureAudio() {
    setState("idle", t("status.requesting_mic"));
    if (!this.mic.stream) await this.mic.start();
    this.mic.setMuted(false);
    if (els.micBtn) els.micBtn.disabled = false;
    this._renderMicBtn();
    this._installMediaSenders();
    this._renderMediaButtons();

    if (this.modelConnected) setState("connected", t("status.audio_captured_connected"));
    else setState("idle", t("status.audio_captured_idle"));
    logDebug("browser", "audio_capture_started");
  }

  async stopAudio() {
    this.mic.onChunk = null;
    await this.mic.stop();
    this.mic.muted = true;
    if (els.micBtn) els.micBtn.disabled = true;
    this._renderMicBtn();
    this._renderMediaButtons();

    if (this.modelConnected) setState("connected", t("status.audio_stopped_connected"));
    else
      setState(
        "idle",
        this._hasVideo() ? t("status.audio_stopped_video_ready") : t("status.audio_stopped_idle"),
      );
    logDebug("browser", "audio_capture_stopped");
  }

  _installMediaSenders() {
    this.mic.onChunk = (ab) => {
      if (!this.modelConnected) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      // Scan isolation: while the app is in a document-scan step the
      // observer session must not be polluted with mic chunks.
      if (this.observerMediaPausedForScan) return;

      // Backpressure: if the WS buffer is already full, drop this chunk and
      // log it. Audio quality matters, but unbounded buffer growth is worse.
      if (this.ws.bufferedAmount > WS_DROP_AUDIO_BUFFER_BYTES) {
        noteAudioChunkDropped(this.ws.bufferedAmount);
        return;
      }

      noteBrowserAudioChunk(ab.byteLength);
      this.ws.send(ab);
    };

    this.videoSources.onFrame = (frame) => {
      if (!SEND_CONTINUOUS_VIDEO_FRAMES) return;
      if (!this.modelConnected) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      // Scan isolation: continuous main-source frames are suppressed during
      // a document-scan step so the observer session sees no Source 1
      // imagery while the scanner runs in its own clean Live session.
      if (this.observerMediaPausedForScan) return;

      // Backpressure: image frames are larger than audio, so we drop them
      // earlier. Stale frames are not useful — dropping is the right policy.
      if (this.ws.bufferedAmount > WS_DROP_IMAGE_BUFFER_BYTES) {
        noteImageFrameDropped(/*frameIndex=*/ -1, this.ws.bufferedAmount);
        return;
      }

      noteBrowserImageFrame(frame);
      this.ws.send(JSON.stringify(imageMessage(frame)));
      // Mirror the exact frame sent into the debug "current view" preview so an
      // operator can see precisely what the model receives (cropped/composite).
      setDebugCurrentView?.(frame);
    };
  }

  // ---- WS lifecycle --------------------------------------------------------

  async connect({ voice, thinkingLevel, includeThoughts }) {
    this.modelConnected = false;
    if (this.videoSources) this.videoSources.setModelConnected(false);
    this.turnInProgress = false;
    this.workflowPromptShown = false;
    this.workflowStarted = false;
    this.workflowState = null;
    this.workflowFinishPending = false;
    this.workflowFinishedUi = false;
    this.closeReason = t("status.disconnected");
    // A fresh connection always starts clean of scan-isolation state — any
    // prior session's scan loop must not leak into the new one.
    this._stopDocumentScanLoop("connect_reset");

    this._syncMediaState();
    this._renderMediaButtons();

    setState("connecting", t("status.connecting"));
    setDebugWsState("opening");

    const runtimeConfig = this.runtimeConfig || (await ensureRuntimeConfig());
    this.runtimeConfig = runtimeConfig;
    const url = resolveWsUrl({ runtimeConfig });
    if (this.videoSources && typeof this.videoSources.setObserverVideoConfig === "function") {
      this.videoSources.setObserverVideoConfig(this.workflowConfig?.observer_video || null);
    }

    this.ws = new WebSocket(url);
    this.ws.binaryType = "arraybuffer";
    this.ws.addEventListener("message", (ev) => this._onMessage(ev));
    this.ws.addEventListener("close", () => this._onClose());
    this.ws.addEventListener("error", () => this._onError("connection error"));

    await new Promise((resolve, reject) => {
      this.ws.addEventListener("open", resolve, { once: true });
      this.ws.addEventListener("error", () => reject(new Error("websocket error")), {
        once: true,
      });
    });

    setDebugWsState("open");
    logDebug("browser", "websocket_open", { url });

    const startPayload = startMessage({
      voice,
      mode: this._currentMode(),
      thinkingLevel,
      includeThoughts,
      workflowId: selectedWorkflowId(),
      language: getLanguage(),
      frontendName: runtimeConfig.frontendName,
      frontendVersion: runtimeConfig.frontendVersion,
      protocolVersion: runtimeConfig.protocolVersion,
    });
    this.ws.send(JSON.stringify(startPayload));
    logDebug("browser -> server", "start", startPayload);

    this._installMediaSenders();
    this._renderMediaButtons();

    // Recorder integration: if the user armed workflow capture, this is the
    // moment recording begins (the WebSocket is open and the start payload
    // is on the wire). Failures must not abort the live connection.
    if (this.recorder && typeof this.recorder.isWorkflowArmed === "function") {
      try {
        if (this.recorder.isWorkflowArmed()) {
          void this.recorder
            .startWorkflowCapture({
              liveSession: this,
              videoSources: this.videoSources,
              workflowState: { workflow_id: selectedWorkflowId() },
            })
            .catch((e) => {
              logDebug(
                "browser",
                "recorder_start_failed",
                { message: e?.message || String(e) },
                "warn",
              );
            });
        }
      } catch (e) {
        logDebug(
          "browser",
          "recorder_start_failed",
          { message: e?.message || String(e) },
          "warn",
        );
      }
    }
  }

  _onMessage(ev) {
    if (ev.data instanceof ArrayBuffer) {
      noteModelAudioChunk(ev.data.byteLength);
      // Silent mode is playback-only: we still receive (and count) the model's
      // audio so the backend/model interaction is untouched, we just don't play
      // it. Everything else (state, transcripts, turn lifecycle) is unchanged.
      if (!this.silentMode) this.player.enqueue(ev.data);

      if (this.workflowFinishPending || this.workflowFinishedUi) {
        renderStartStopButton(false);
        if (els.dbgState) els.dbgState.textContent = "idle";
        if (els.statusLine) els.statusLine.textContent = t("status.finishing");
      } else {
        setState("speaking", t("status.speaking"));
      }
      return;
    }

    let msg;
    try {
      msg = JSON.parse(ev.data);
    } catch (_) {
      logDebug("server -> browser", "bad_json", { raw: String(ev.data).slice(0, 500) }, "warn");
      return;
    }

    switch (msg.type) {
      case "workflow_prompt":
        logDebug("server -> browser", "workflow_prompt", {
          workflow_id: msg.workflow_id,
          title: msg.title,
          chars: msg.prompt.length,
        });
        if (!this.workflowPromptShown) {
          appendMessage("prompt", `SYSTEM PROMPT\n\n${msg.prompt}`);
          this.workflowPromptShown = true;
        }
        break;

      case "workflow_state":
        logDebug("server -> browser", "workflow_state", msg);
        this.workflowState = msg;
        renderWorkflowState(msg);
        this._handleWorkflowStateForScan(msg);
        this._sendWorkflowStartOnce();
        break;

      case "workflow_finished": {
        logDebug("server -> browser", "workflow_finished", msg);
        this.workflowFinishPending = true;
        this.workflowFinishedUi = true;
        this._stopAutoChecks();
        this._stopDocumentScanLoop("workflow_finished");
        setWorkflowControlsEnabled(false);
        if (msg.reset_step) {
          this.workflowState = msg.reset_step;
          renderWorkflowState(msg.reset_step);
        }
        setState("idle", t("status.finished_ready"));
        renderStartStopButton(false);
        if (els.connectBtn) els.connectBtn.disabled = false;
        // Recorder integration: workflow_finished is the natural one-shot
        // stop for workflow-mode capture. Free capture is unaffected.
        if (this.recorder && typeof this.recorder.stopWorkflowCapture === "function") {
          void this.recorder
            .stopWorkflowCapture("workflow_finished")
            .catch(() => {});
        }
        break;
      }

      case "document_scan_status":
        logDebug("server -> browser", "document_scan_status", msg);
        this._handleDocumentScanStatus(msg);
        break;

      case "proof_request":
        logDebug("server -> browser", "proof_request", {
          request_id: msg.request_id,
          purpose: msg.purpose,
          stage: msg.stage,
          source_index: msg.source_index,
        });
        void this._answerProofRequest(msg);
        break;

      case "proof_result":
        logDebug("server -> browser", "proof_result", {
          request_id: msg.request_id,
          record_id: msg.record_id,
          stage: msg.stage,
          verdict: msg.verdict,
          code: msg.code,
          status: msg.status,
        });
        this._handleProofResult(msg);
        break;

      case "packing_status":
        logDebug("server -> browser", "packing_status", {
          order_key: msg.order_key,
          checks: msg.checks,
          proofs: (msg.proofs || []).length,
          event: msg.event,
        });
        if (this.packingPanel) this.packingPanel.setStatus(msg);
        break;

      case "status":
        logDebug("server -> browser", "status", msg);
        if (msg.message === "connected") {
          this.modelConnected = true;
          if (this.videoSources) this.videoSources.setModelConnected(true);
          setState("connected", t("status.connected"));
          if (els.chatInput) els.chatInput.disabled = true;
          if (els.chatSend) els.chatSend.disabled = true;
          if (els.workflowSel) els.workflowSel.disabled = true;
          setWorkflowControlsEnabled(true);
          this._sendWorkflowStartOnce();
          // The backend knows nothing about the order on the table yet.
          this._sendTrainerContext({ force: true });
        } else {
          this.modelConnected = false;
          if (this.videoSources) this.videoSources.setModelConnected(false);
          // Server-supplied progress text is intentionally surfaced verbatim
          // (it includes voice/thinking metadata in English) — only the
          // "connected" final state is translated to the user-friendly UI line.
          setState("connecting", msg.message);
        }
        break;

      case "text":
        logDebug("server -> browser", "model_text", {
          chars: msg.text.length,
          text: msg.text,
        });
        this._appendStreamingText("model", msg.text);
        break;

      case "transcript":
        logDebug("server -> browser", `${msg.role}_transcript`, {
          role: msg.role,
          chars: msg.text.length,
          text: msg.text,
        });
        if (msg.role === "model" || msg.role === "user") {
          this._appendStreamingText(msg.role, msg.text);
        }
        break;

      case "thought":
        logDebug("server -> browser", "thought_summary", {
          chars: msg.text.length,
          text: msg.text,
        });
        break;

      case "debug":
        handleServerDebug(msg.event);
        break;

      case "step_signal":
        logDebug("server -> browser", "step_signal_requested", {
          direction: msg.direction,
          duration_ms: msg.duration_ms,
        });
        this._flashStepIndicator(msg.direction, msg.duration_ms);
        break;

      case "interrupt_notice":
        logDebug("server -> browser", "interrupt_notice_requested", {
          text: msg.text,
          duration_ms: msg.duration_ms,
        });
        this._showInterruptNotice(msg.text, msg.duration_ms);
        break;

      case "interrupted":
        logDebug("server -> browser", "interrupted");
        this.turnInProgress = false;
        this.player.stopAll();
        this._closeStreamingBubble({ interrupted: true });
        setState("connected", t("status.observing"));
        break;

      case "turn_complete":
        logDebug("server -> browser", "turn_complete");
        this.turnInProgress = false;
        this._closeStreamingBubble();
        if (this.workflowFinishPending || this.workflowFinishedUi) {
          void this.disconnect("finished.", { stopMedia: false, stopPlayback: false });
        } else {
          setState("connected", t("status.observing"));
        }
        break;

      case "error":
        this.modelConnected = false;
        this.turnInProgress = false;
        this._stopAutoChecks();
        this._stopDocumentScanLoop("server_error");
        setWorkflowControlsEnabled(false);
        logDebug("server -> browser", "error", msg, "error");
        appendMessage("error", t("status.error_prefix", { msg: msg.message }));
        setState("error", t("status.error_prefix", { msg: msg.message }));
        break;

      default:
        logDebug("server -> browser", "unknown_message", msg, "warn");
        break;
    }
  }

  _appendStreamingText(role, text) {
    if (!text || !els.chatLog) return;
    const last = els.chatLog.lastElementChild;
    if (
      last &&
      last.classList.contains("msg") &&
      last.classList.contains(role) &&
      last.dataset.streaming === "1"
    ) {
      last.textContent += text;
      els.chatLog.scrollTop = els.chatLog.scrollHeight;
      return;
    }
    this._closeStreamingBubble();
    const div = appendMessage(role, text);
    if (div) div.dataset.streaming = "1";
  }

  _closeStreamingBubble({ interrupted = false } = {}) {
    if (!els.chatLog) return;
    const last = els.chatLog.lastElementChild;
    if (!last || last.dataset.streaming !== "1") return;
    delete last.dataset.streaming;
    if (interrupted) last.classList.add("interrupted");
  }

  _flashStepIndicator(direction, durationMs = 1000) {
    const key = direction === "prev" ? "prev" : "next";
    const indicator = key === "prev" ? els.prevStepIndicator : els.nextStepIndicator;
    const ms = Math.max(250, Math.min(5000, Number(durationMs) || 1000));
    if (!indicator) {
      logDebug(
        "browser",
        "step_signal_indicator_missing",
        { direction: key, duration_ms: ms },
        "warn",
      );
      return;
    }
    if (this.stepSignalTimers[key]) {
      clearTimeout(this.stepSignalTimers[key]);
      this.stepSignalTimers[key] = null;
    }
    indicator.dataset.state = "active";
    flashStepCardMotion(`complete-${key}`, Math.min(ms, 650));
    logDebug("browser", "step_signal_shown", { direction: key, duration_ms: ms });
    this.stepSignalTimers[key] = setTimeout(() => {
      indicator.dataset.state = "standby";
      this.stepSignalTimers[key] = null;
      logDebug("browser", "step_signal_hidden", { direction: key });
    }, ms);
  }

  _showInterruptNotice(text, durationMs = 5000) {
    const notice = els.interruptNotice;
    const noticeText = els.interruptNoticeText;
    const safeText = String(text || "").trim() || "Interrupt.";
    const ms = Math.max(1000, Math.min(15000, Number(durationMs) || 5000));
    if (!notice || !noticeText) {
      logDebug(
        "browser",
        "interrupt_notice_missing",
        { text: safeText, duration_ms: ms },
        "warn",
      );
      return;
    }
    if (this.interruptNoticeTimer) {
      clearTimeout(this.interruptNoticeTimer);
      this.interruptNoticeTimer = null;
    }
    noticeText.textContent = safeText;
    notice.hidden = false;
    notice.dataset.state = "active";
    logDebug("browser", "interrupt_notice_shown", { text: safeText, duration_ms: ms });
    this.interruptNoticeTimer = setTimeout(() => {
      notice.hidden = true;
      notice.dataset.state = "hidden";
      this.interruptNoticeTimer = null;
      logDebug("browser", "interrupt_notice_hidden");
    }, ms);
  }

  _startAutoChecks() {
    if (this.autoCheckTimer) return;
    this.autoCheckTimer = setInterval(() => {
      if (!this.modelConnected) return;
      if (this.turnInProgress) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      // WMS events or the backend's photo check complete these steps; a CHECK
      // turn would only invite the observer to guess.
      if (this._activeStepAppControlled()) return;
      this.sendStepInstruction("check", { displayKind: "auto", allowDuringTurn: false });
    }, AUTO_CHECK_INTERVAL_MS);
    logDebug("browser", "auto_check_started", {
      interval_ms: AUTO_CHECK_INTERVAL_MS,
      action: "check",
    });
  }

  _stopAutoChecks() {
    if (!this.autoCheckTimer) return;
    clearInterval(this.autoCheckTimer);
    this.autoCheckTimer = null;
    logDebug("browser", "auto_check_stopped");
  }

  _sendWorkflowStartOnce() {
    if (this.workflowStarted) return;
    if (!this.workflowPromptShown) return;
    if (!this.workflowState) return;
    if (!this.modelConnected) return;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.workflowStarted = true;
    this.sendStepInstruction(AUTO_START_ACTION, {
      displayKind: "auto",
      allowDuringTurn: true,
    });
    this._startAutoChecks();
  }

  // ---- Document-scan loop ---------------------------------------------------

  // Inspect a workflow_state payload and start/stop the scan loop accordingly.
  // Generic over workflow id — driven entirely by `kind === "document_scan"`.
  _handleWorkflowStateForScan(state) {
    const kind = state && state.kind;
    if (kind === "document_scan") {
      const cfg = state.document_scan || {};
      const stepId = state.step_id || null;
      this._startDocumentScanLoop(cfg, stepId);
    } else if (this.documentScanActive) {
      this._stopDocumentScanLoop("workflow_state_left_scan_step");
    }
  }

  _startDocumentScanLoop(cfg, stepId) {
    // Re-entry into the same scan step is a no-op; the loop is already
    // armed.
    if (this.documentScanActive && this.documentScanStepId === stepId) return;
    if (this.documentScanActive) {
      this._stopDocumentScanLoop("scan_step_changed");
    }

    this.documentScanActive = true;
    this.documentScanAttempt = 0;
    this.documentScanWaiting = false;
    this.documentScanStepId = stepId;
    this.documentScanConfig = {
      sourceIndex: Number(cfg.source_index) || DOCUMENT_SCAN_SOURCE_INDEX,
      retryMs: Number(cfg.retry_ms) || DOCUMENT_SCAN_RETRY_MS,
      maxDim: Number(cfg.max_dim) || DOCUMENT_SCAN_MAX_DIM,
      jpegQuality:
        typeof cfg.jpeg_quality === "number"
          ? cfg.jpeg_quality
          : DOCUMENT_SCAN_JPEG_QUALITY,
    };

    // Pause auto-checks and observer media before the first attempt so the
    // observer session is clean of Source 1 frames during scanning.
    this._stopAutoChecks();
    if (!this.observerMediaPausedForScan) {
      this.observerMediaPausedForScan = true;
      logDebug("browser", "observer_media_paused_for_scan", {
        step_id: stepId,
        source_index: this.documentScanConfig.sourceIndex,
      });
    }

    setState(
      "connected",
      t("status.scan_place_doc", { n: this.documentScanConfig.sourceIndex }),
    );
    logDebug("browser", "document_scan_loop_started", {
      step_id: stepId,
      retry_ms: this.documentScanConfig.retryMs,
      source_index: this.documentScanConfig.sourceIndex,
    });

    this._scheduleDocumentScanTick(0);
  }

  _stopDocumentScanLoop(reason) {
    if (!this.documentScanActive && !this.observerMediaPausedForScan) return;
    if (this.documentScanTimer) {
      clearTimeout(this.documentScanTimer);
      this.documentScanTimer = null;
    }
    const wasActive = this.documentScanActive;
    this.documentScanActive = false;
    this.documentScanWaiting = false;
    this.documentScanStepId = null;
    this.documentScanConfig = null;
    if (this.observerMediaPausedForScan) {
      this.observerMediaPausedForScan = false;
      logDebug("browser", "observer_media_resumed_after_scan", { reason });
    }
    if (wasActive) {
      logDebug("browser", "document_scan_loop_stopped", { reason });
    }
    // Resume auto-checks for the (possibly newly-materialized) post-scan
    // step, but only if we are still connected to a workflow that wants
    // them.
    if (this.modelConnected && this.workflowStarted && !this.workflowFinishedUi) {
      this._startAutoChecks();
    }
  }

  _scheduleDocumentScanTick(delayMs) {
    if (this.documentScanTimer) {
      clearTimeout(this.documentScanTimer);
      this.documentScanTimer = null;
    }
    if (!this.documentScanActive) return;
    this.documentScanTimer = setTimeout(
      () => {
        this.documentScanTimer = null;
        void this._documentScanTick();
      },
      Math.max(0, Number(delayMs) || 0),
    );
  }

  async _documentScanTick() {
    if (!this.documentScanActive) return;
    const cfg = this.documentScanConfig;
    if (!cfg) return;
    if (this.documentScanWaiting) return;
    if (!this.modelConnected) {
      this._scheduleDocumentScanTick(cfg.retryMs);
      return;
    }
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this._scheduleDocumentScanTick(cfg.retryMs);
      return;
    }
    if (!this.videoSources || !this.videoSources.hasActiveSourceAt(cfg.sourceIndex)) {
      setState("connected", t("status.scan_source_2_required"));
      logDebug("browser", "document_scan_source_missing", {
        source_index: cfg.sourceIndex,
      });
      this._scheduleDocumentScanTick(cfg.retryMs);
      return;
    }

    let frame;
    try {
      frame = await this.videoSources.captureDocumentFrameByIndex(cfg.sourceIndex, {
        maxDim: cfg.maxDim,
        jpegQuality: cfg.jpegQuality,
        flashUsed: true,
      });
    } catch (e) {
      logDebug(
        "browser",
        "document_scan_capture_error",
        { message: e?.message || String(e) },
        "warn",
      );
      this._scheduleDocumentScanTick(cfg.retryMs);
      return;
    }
    if (!frame) {
      this._scheduleDocumentScanTick(cfg.retryMs);
      return;
    }

    this.documentScanAttempt += 1;
    this.documentScanWaiting = true;

    const payload = documentScanImageMessage(frame, {
      workflowId: this.workflowState && this.workflowState.workflow_id,
      stepId: this.documentScanStepId,
      attempt: this.documentScanAttempt,
    });
    try {
      this.ws.send(JSON.stringify(payload));
      logDebug("browser -> server", "document_scan_image_sent", {
        attempt: this.documentScanAttempt,
        source_index: cfg.sourceIndex,
        width: frame.width,
        height: frame.height,
        byte_length: frame.byte_length,
      });
    } catch (e) {
      this.documentScanWaiting = false;
      logDebug(
        "browser",
        "document_scan_send_failed",
        { message: e?.message || String(e) },
        "warn",
      );
      this._scheduleDocumentScanTick(cfg.retryMs);
    }
  }

  _handleDocumentScanStatus(msg) {
    const cfg = this.documentScanConfig;
    const retryMs = (cfg && cfg.retryMs) || DOCUMENT_SCAN_RETRY_MS;
    const unreadableFallback = t("status.scan_unreadable");
    switch (msg.state) {
      case "submitted":
        setState("connected", t("status.scan_in_progress"));
        break;
      case "missing_source":
        this.documentScanWaiting = false;
        setState("connected", t("status.scan_source_2_required"));
        this._scheduleDocumentScanTick(retryMs);
        break;
      case "unreadable":
        this.documentScanWaiting = false;
        setState("connected", msg.message || unreadableFallback);
        appendMessage("system", msg.message || unreadableFallback);
        this._scheduleDocumentScanTick(retryMs);
        break;
      case "success":
        // The matching workflow_state will fire ``_handleWorkflowStateForScan``
        // and stop the loop; until then leave waiting cleared so we don't
        // accept further attempts.
        this.documentScanWaiting = false;
        break;
      case "error":
        this.documentScanWaiting = false;
        setState(
          "connected",
          msg.message
            ? t("status.scan_error_with_message", { msg: msg.message })
            : t("status.scan_error_generic"),
        );
        // Errors are usually transient (mime/source mismatch can recover
        // once the user fixes Source 2). Schedule another attempt.
        this._scheduleDocumentScanTick(retryMs);
        break;
      default:
        // Unknown states are ignored — older servers may emit something
        // newer clients don't recognise.
        break;
    }
  }

  // ---- Packing proof ------------------------------------------------------
  //
  // Workflows with `packing_proof` are driven by the app, not by CHECK turns:
  // the how.fm Trainer reports the SwiftLOG order (context), the backend asks
  // for high-resolution crate stills (proof_request) and answers with
  // assessed proof photos (proof_result) that the Trainer archives locally.

  _activeStepAppControlled() {
    const control = this.workflowState && this.workflowState.control;
    return control === "wms" || control === "photo_check";
  }

  _onTrainerContext(payload) {
    if (!payload) return;
    if (this.packingPanel) this.packingPanel.setContext(payload.context);
    this._sendTrainerContext({ events: payload.events || [] });
  }

  // Events always go out; an unchanged snapshot without events is skipped.
  _sendTrainerContext({ events = [], force = false } = {}) {
    if (!this.packingEnabled || !this.modelConnected) return false;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    const latest = this.trainer && this.trainer.latest;
    if (!latest || !latest.context) return false;
    const signature = JSON.stringify({
      wms: latest.context.wms || null,
      slam: latest.context.slam || null,
    });
    if (!force && !events.length && signature === this.lastContextSignature) return false;
    this.lastContextSignature = signature;
    const msg = contextMessage(latest.context, events);
    this.ws.send(JSON.stringify(msg));
    logDebug("browser -> server", "context", {
      events: msg.events,
      phase: latest.context.wms?.phase,
      identifier: latest.context.wms?.identifier,
    });
    return true;
  }

  _onTrainerTrigger(trigger) {
    if (!trigger || trigger.kind !== "proof") return;
    logDebug("browser", "trainer_trigger", trigger);
    void this.captureWorkerProof(trigger.source === "buzzer" ? "buzzer" : "manual");
  }

  async _answerProofRequest(msg) {
    const frame = await this._captureProofFrame(msg.source_index, {
      maxDim: msg.max_dim,
      jpegQuality: msg.jpeg_quality,
      // The monitor asks every few seconds; flashing the card each time
      // would only distract the worker.
      flashUsed: msg.purpose !== "monitor",
    });
    if (!frame) return false;
    return this._sendProofStill(frame, {
      requestId: msg.request_id,
      purpose: msg.purpose,
      stage: msg.stage,
    });
  }

  // Worker-triggered proof photo: the USB buzzer or the panel button.
  async captureWorkerProof(purpose = "manual") {
    if (!this.packingEnabled || !this.modelConnected) return false;
    const cfg = this.workflowConfig?.packing_proof || {};
    const frame = await this._captureProofFrame(cfg.source_index || 1, {
      maxDim: cfg.max_dim || 1920,
      jpegQuality: cfg.jpeg_quality || 0.9,
      flashUsed: true,
    });
    if (!frame) return false;
    this.proofClientSeq += 1;
    return this._sendProofStill(frame, {
      requestId: `client-${this.proofClientSeq}`,
      purpose,
      stage: "manual",
    });
  }

  async _captureProofFrame(sourceIndex, { maxDim, jpegQuality, flashUsed }) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return null;
    const index = Number(sourceIndex) || 1;
    let frame = null;
    try {
      frame = await this.videoSources.captureDocumentFrameByIndex(index, {
        maxDim,
        jpegQuality,
        flashUsed,
      });
    } catch (e) {
      logDebug("browser", "proof_capture_error", { message: e?.message || String(e) }, "warn");
    }
    if (frame) return frame;
    const now = Date.now();
    if (now - this.proofSourceWarnedAt > 15000) {
      this.proofSourceWarnedAt = now;
      const text = t("status.proof_source_missing", { n: index });
      setState("connected", text);
      appendMessage("system", text);
    }
    return null;
  }

  _sendProofStill(frame, { requestId, purpose, stage }) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    const capturedAtMs = Date.now();
    this.proofImages.set(requestId, { mime_type: frame.mime_type, data: frame.data, at: capturedAtMs });
    this._pruneProofImages();
    this.ws.send(
      JSON.stringify(proofImageMessage(frame, { requestId, purpose, stage, capturedAtMs })),
    );
    if (this.packingPanel && purpose !== "monitor") this.packingPanel.setChecking(true);
    logDebug("browser -> server", "proof_image_sent", {
      request_id: requestId,
      purpose,
      stage,
      width: frame.width,
      height: frame.height,
      byte_length: frame.byte_length,
    });
    return true;
  }

  // Monitor stills that never become proofs age out; a proof_result arrives
  // within seconds of its still.
  _pruneProofImages() {
    const cutoff = Date.now() - 120000;
    for (const [id, entry] of this.proofImages) {
      if (entry.at >= cutoff && this.proofImages.size <= 24) break;
      this.proofImages.delete(id);
    }
  }

  _handleProofResult(msg) {
    if (this.packingPanel) this.packingPanel.setChecking(false);
    if (!msg || msg.status !== "ok") {
      if (msg && msg.message) appendMessage("system", `proof photo: ${msg.message}`);
      return;
    }
    const cached = msg.request_id ? this.proofImages.get(msg.request_id) : null;
    const imageUrl = cached ? `data:${cached.mime_type};base64,${cached.data}` : null;
    if (this.packingPanel) this.packingPanel.addProof(msg, imageUrl);
    if (msg.message && msg.verdict !== "fail") appendMessage("system", msg.message);
    if (!msg.save || !cached || !this.trainer.available) return;
    const { type: _type, save: _save, ...record } = msg;
    void this.trainer
      .saveProof({
        ...record,
        workflow_id: this.workflowConfig?.id || null,
        image: { mime_type: cached.mime_type, data: cached.data },
      })
      .then((result) => {
        if (this.packingPanel) this.packingPanel.markSaved(msg.record_id, result);
        logDebug(
          "browser",
          "proof_saved",
          {
            record_id: msg.record_id,
            ok: !!result?.ok,
            path: result?.path || null,
            reason: result?.reason || null,
          },
          result?.ok ? "info" : "warn",
        );
      });
  }

  _observerVideoReadyForCheck(action) {
    const normalized = String(action || "check").toLowerCase();
    if (normalized !== "check") return true;
    const cfg = this.workflowConfig?.observer_video;
    if (!cfg || cfg.mode !== "composite_vertical") return true;
    if (!this.videoSources || typeof this.videoSources.observerVideoReady !== "function") {
      return false;
    }
    if (this.videoSources.observerVideoReady(cfg)) return true;
    if (typeof this.videoSources.warnRequiredCompositeSources === "function") {
      this.videoSources.warnRequiredCompositeSources(cfg);
    }
    return false;
  }

  sendStepInstruction(action, { displayKind = "auto", allowDuringTurn = true } = {}) {
    if (!this._observerVideoReadyForCheck(action)) return false;
    const modelText = buildModelStepInstruction(action, this.workflowState);
    return this.sendText(action, {
      displayKind,
      allowDuringTurn,
      modelText,
      displayText: action,
    });
  }

  sendText(
    text,
    { displayKind = "user", allowDuringTurn = true, modelText = null, displayText = null } = {},
  ) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    if (!allowDuringTurn && this.turnInProgress) return false;

    const outgoingText = modelText || text;
    const visibleText = displayText || text;

    this._closeStreamingBubble({ interrupted: true });
    this.player.stopAll();
    appendMessage(displayKind, visibleText);

    this.turnInProgress = true;
    this.ws.send(JSON.stringify(textMessage(outgoingText)));
    logDebug("browser -> server", "workflow_text_sent", {
      text: outgoingText,
      visible_text: visibleText,
      displayKind,
    });
    return true;
  }

  toggleMic() {
    this.mic.setMuted(!this.mic.muted);
    this._renderMicBtn();
  }

  _renderMicBtn() {
    if (!els.micBtn) return;
    const muted = this.mic.muted;
    const icon = els.micBtn.querySelector(".icon");
    const label = els.micBtn.querySelector(".label");
    if (icon) icon.dataset.icon = muted ? "mic-off" : "mic";
    if (label) label.textContent = muted ? t("controls.unmute_mic") : t("controls.mute_mic");
  }

  async stopMedia({ reason } = {}) {
    this._stopDocumentScanLoop("stop_media");
    this.mic.onChunk = null;
    if (this.videoSources) {
      this.videoSources.onFrame = null;
      this.videoSources.resetAllToSingle();
    }
    await this.mic.stop();
    this.mediaPrepared = false;
    this.preparedMode = "none";
    this.mic.muted = true;
    if (els.micBtn) els.micBtn.disabled = true;
    this._renderMicBtn();
    this._renderMediaButtons();

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      setState("connected", t("status.media_stopped_text_chat"));
    } else {
      setState("idle", reason || t("status.disconnected"));
    }
    logDebug("browser", "all_media_stopped");
  }

  async disconnect(reason, { stopMedia = false, stopPlayback = true } = {}) {
    const closeReason = reason || t("status.disconnected");
    logDebug("browser", "disconnect_requested", { reason: closeReason });

    // Recorder integration: any in-flight workflow capture must finalize
    // here. Free capture is independent of WebSocket lifecycle and is left
    // alone — the user must stop it explicitly.
    if (this.recorder && typeof this.recorder.stopWorkflowCapture === "function") {
      void this.recorder
        .stopWorkflowCapture(`disconnect:${reason}`)
        .catch(() => {});
    }

    this.modelConnected = false;
    if (this.videoSources) this.videoSources.setModelConnected(false);
    this.turnInProgress = false;
    this.workflowPromptShown = false;
    this.workflowStarted = false;
    this.workflowFinishPending = false;
    this.workflowFinishedUi = false;
    this.closeReason = closeReason;
    this._stopAutoChecks();
    this._stopDocumentScanLoop("disconnect");
    setWorkflowControlsEnabled(false);
    if (els.workflowSel) els.workflowSel.disabled = false;

    const ws = this.ws;
    this.ws = null;
    if (ws) {
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(stopMessage()));
          logDebug("browser -> server", "stop");
        }
        ws.close();
      } catch (_) {}
    }
    setDebugWsState("closed");

    if (stopPlayback) this.player.stopAll();
    this.mic.onChunk = null;
    if (this.videoSources) this.videoSources.onFrame = null;

    for (const key of ["next", "prev"]) {
      if (this.stepSignalTimers[key]) {
        clearTimeout(this.stepSignalTimers[key]);
        this.stepSignalTimers[key] = null;
      }
    }
    if (els.nextStepIndicator) els.nextStepIndicator.dataset.state = "standby";
    if (els.prevStepIndicator) els.prevStepIndicator.dataset.state = "standby";
    clearStepCardMotion();

    if (this.interruptNoticeTimer) {
      clearTimeout(this.interruptNoticeTimer);
      this.interruptNoticeTimer = null;
    }
    if (els.interruptNotice) {
      els.interruptNotice.hidden = true;
      els.interruptNotice.dataset.state = "hidden";
    }

    if (els.chatInput) els.chatInput.disabled = true;
    if (els.chatSend) els.chatSend.disabled = true;

    if (stopMedia) {
      await this.stopMedia({ reason: closeReason });
    } else if (this.mediaPrepared) {
      if (els.micBtn) els.micBtn.disabled = !this._hasAudio();
      this._renderMediaButtons();
      setState("idle", t("status.media_is_ready", { reason: closeReason }));
    } else {
      if (els.micBtn) els.micBtn.disabled = true;
      this._renderMediaButtons();
      setState("idle", closeReason);
    }

    if (els.connectBtn) els.connectBtn.disabled = false;
    renderStartStopButton(false);
  }

  _onClose() {
    setDebugWsState("closed");
    logDebug("browser", "websocket_closed");
    // Recorder integration: a peer-side close should also finalize a
    // workflow capture that was running.
    if (this.recorder && typeof this.recorder.stopWorkflowCapture === "function") {
      void this.recorder
        .stopWorkflowCapture("websocket_closed")
        .catch(() => {});
    }
    this.ws = null;
    this.modelConnected = false;
    if (this.videoSources) this.videoSources.setModelConnected(false);
    this.turnInProgress = false;
    this.workflowPromptShown = false;
    this.workflowStarted = false;
    this.workflowFinishPending = false;
    this.workflowFinishedUi = false;
    this._stopAutoChecks();
    this._stopDocumentScanLoop("websocket_closed");
    setWorkflowControlsEnabled(false);
    if (els.workflowSel) els.workflowSel.disabled = false;
    if (els.chatInput) els.chatInput.disabled = true;
    if (els.chatSend) els.chatSend.disabled = true;

    const reason = this.closeReason || t("status.disconnected");
    this._renderMediaButtons();
    if (this.mediaPrepared) setState("idle", t("status.media_is_ready", { reason }));
    else setState("idle", reason);

    this.closeReason = t("status.disconnected");
    if (els.connectBtn) els.connectBtn.disabled = false;
    renderStartStopButton(false);
  }

  _onError(msg) {
    this.modelConnected = false;
    if (this.videoSources) this.videoSources.setModelConnected(false);
    logDebug("browser", "websocket_error", { message: msg }, "error");
    appendMessage("error", msg);
    setState("error", msg);
  }
}
